import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type React from 'react'
import { useParams } from 'react-router-dom'
import { FileUploadModal } from '../../../../components/FileUploadModal'
import type { FileUploadRequest } from '../../../../components/FileUploadModal/types'
import { useToast } from '../../../../utils/ToastContext'
import { trpc } from '../../../../utils/trpc'

const ALLOWED_FILE_TYPES = [
	'application/vnd.ms-excel',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	'text/csv',
]

const FILE_EXTENSIONS = '.csv, .xls, .xlsx'

interface CycleReviewersFileUploadModalProps {
	isOpen: boolean
	onClose: () => void
	onUploadSuccess: () => void
}

export const CycleReviewersFileUploadModal: React.FC<CycleReviewersFileUploadModalProps> = ({
	isOpen,
	onClose,
	onUploadSuccess,
}) => {
	const { cycleId, organizationId } = useParams<{ cycleId: string; organizationId: string }>()
	const queryClient = useQueryClient()
	const toast = useToast()

	const { data: groups } = useQuery({
		...trpc.admin.cycles.reviewerGroups.search.queryOptions({
			cycleId: cycleId as string,
			search: '',
			perPage: 99999,
		}),
		enabled: !!cycleId,
	})

	const uploadRequestMutation = useMutation({
		...trpc.admin.cycleReviewerFiles.requestFileUpload.mutationOptions(),
	})

	const acknowledgeUploadMutation = useMutation({
		...trpc.admin.cycleReviewerFiles.acknowledgeFileUpload.mutationOptions(),
	})

	const validateFileMutation = useMutation({
		...trpc.admin.cycleReviewerFiles.validateFile.mutationOptions(),
	})

	const processFileMutation = useMutation({
		...trpc.admin.cycleReviewerFiles.processFile.mutationOptions({
			onSuccess: () => {
				toast.showToast('Reviewer list uploaded successfully')
			},
			onError: () => {
				toast.showToast('Error uploading reviewer list', 'error')
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.reviewers.search.queryKey({ cycleId: cycleId as string }),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	})

	const uploadRequest: FileUploadRequest = {
		requestUpload: (data) =>
			uploadRequestMutation.mutateAsync({
				...data,
				cycleId: cycleId as string,
				organizationId: organizationId as string,
			}),
		acknowledgeUpload: (data) =>
			acknowledgeUploadMutation.mutateAsync({
				...data,
				cycleId: cycleId as string,
			}),
		validateFile: (data) =>
			validateFileMutation.mutateAsync({
				fileId: data.fileId,
				cycleId: cycleId as string,
				participantExternalIdHeader: data.columnMapping.participantExternalIdHeader,
				reviewerExternalIdHeader: data.columnMapping.reviewerExternalIdHeader,
				groupHeader: data.columnMapping.groupHeader,
			}),
		processFile: async (data) => {
			await processFileMutation.mutateAsync({
				fileId: data.fileId,
				cycleId: cycleId as string,
				groups: Object.keys(data.groupsMapping || {}).map((name) => ({
					cycleReviewerGroupId: data.groupsMapping?.[name] || '',
					name,
				})),
				participantExternalIdHeader: data.columnMapping.participantExternalIdHeader,
				reviewerExternalIdHeader: data.columnMapping.reviewerExternalIdHeader,
				groupHeader: data.columnMapping.groupHeader,
			})
		},
	}

	const handleClose = () => {
		onClose()
	}

	return (
		<FileUploadModal
			isOpen={isOpen}
			onClose={handleClose}
			onUploadSuccess={onUploadSuccess}
			fileConfig={{
				allowedTypes: ALLOWED_FILE_TYPES,
				extensions: FILE_EXTENSIONS,
				maxSize: 2 * 1024 * 1024,
			}}
			columnMappingFields={[
				{
					key: 'participantExternalIdHeader',
					label: 'Participant ID Column (user being reviewed)',
					required: true,
				},
				{
					key: 'reviewerExternalIdHeader',
					label: 'Reviewer ID Column (user reviewing)',
					required: true,
				},
				{ key: 'groupHeader', label: 'Group Name Column', required: true },
			]}
			enableValidation
			validationColumnMappedFields={groups?.data.map((group) => ({
				id: group.id,
				name: group.name,
			}))}
			uploadRequest={uploadRequest}
			title='Reviewer list file'
			overwriteMessage='Note that uploading a file will overwrite any existing reviewer list'
		/>
	)
}
